import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import * as workListsServices from "../service/WorkLists"
import { WorkQueuesRead } from "../api/generated"
import useShowError from "./useShowError"
import { QUEUE_DROPDOWN_KEY } from "../constants"

const WORK_LISTS = "work_lists"

export const useWorkLists = () =>
  useQuery({
    queryKey: [WORK_LISTS],
    queryFn: () =>workListsServices.getAllWorkLists(),
    placeholderData: [],
  })

function extractDetail(error) {
  if (typeof error.body === "string") {
    try {
      const body = JSON.parse(error.body);
      return body.detail || error.message;
    } catch {
      // body isn't parseable, fall back
      return error.message;
    }
  }

  // if body is already an object (rare case)
  if (typeof error.body === "object" && error.body.detail) {
    return error.body.detail;
  }

  return error.message;
}

export const useCreateWorkList = () => {
  const queryClient = useQueryClient()
  const showError = useShowError()
  return useMutation({
      mutationFn: ({ name, opco_name }: WorkQueuesRead) =>
        workListsServices.createWorkList({ name, opco_name }),
      onSuccess(response) {
        queryClient.setQueryData([WORK_LISTS], (old: Array<WorkQueuesRead>) =>
          old.concat(response),
        )
      },
      onError(error) {
        showError(`Failed to create work list: ${extractDetail(error)}`);
    },
  })
}

export const useUpdateWorkList = () => {
  const queryClient = useQueryClient()
  const showError = useShowError()

  return useMutation({
    mutationFn: ({ id, name, opco_name }: WorkQueuesRead) =>
      workListsServices.updateWorkList(id, { name, opco_name }),
    onSuccess(response) {
      queryClient.setQueryData([WORK_LISTS], (old: Array<WorkQueuesRead>) =>
        old.map((item) => (item.id === response.id ? response : item)),
      )
      queryClient.invalidateQueries({ queryKey: QUEUE_DROPDOWN_KEY });
      queryClient.invalidateQueries({ queryKey: ["opco"] });
    },
    onError(error) {
      showError(`Failed to update work list: ${extractDetail(error)}`)
    },
  })
}

export const useDeleteWorkList = () => {
  const queryClient = useQueryClient()
  const showError = useShowError()

  return useMutation({
    mutationFn: ({ id }: WorkQueuesRead) =>
      workListsServices.deleteWorkList(id),
    onSuccess({ id }) {
      queryClient.setQueryData([WORK_LISTS], (old: Array<WorkQueuesRead>) =>
        old.filter((item) => item.id !== id),
      )
    },
    onError(error) {
      showError(`Failed to delete work list: ${extractDetail(error)}`)
    },
  })
}
